import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Modal from '../Layout/Modal';
import ElevatedButton from '../Layout/ElevatedButton';
import { useAuth } from '../../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API_ENDPOINTS, API_BASE_URL } from '../../api/config';
import { getPricingPlans, splitPricingPlans } from '../../services/pricingPlans';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

const SubscriptionCard = styled.div`
  border: 2px solid ${props => props.selected ? props.theme.buttonPrimary : props.highlight ? '#bfa43a' : props.disabled ? props.theme.border : props.theme.border};
  border-radius: 8px;
  padding: 1.5rem;
  background: ${props => props.selected ? props.theme.background : props.highlight ? 'rgba(191, 164, 58, 0.15)' : props.disabled ? props.theme.cardBackground : props.theme.backgroundSecondary};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  text-align: center;
  position: relative;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    border-color: ${props => props.disabled ? props.theme.border : props.theme.buttonPrimary};
    background: ${props => props.disabled ? props.theme.cardBackground : props.theme.background};
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PlanTitle = styled.h3`
  color: ${props => props.theme.text};
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const PlanPrice = styled.div`
  color: ${props => props.theme.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PlanTotalPrice = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 1rem;
  margin-bottom: 0.5rem;
  text-decoration: ${props => props.hasTax ? 'line-through' : 'none'};
`;

const PlanTaxIncluded = styled.div`
  color: ${props => props.theme.buttonPrimary};
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PlanPeriod = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const PlanFeature = styled.li`
  color: ${props => props.theme.textSecondary};
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  padding-left: 1rem;
  position: relative;

  &:before {
    content: '✓';
    color: ${props => props.theme.buttonPrimary};
    font-weight: bold;
    position: absolute;
    left: 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  margin-bottom: 2rem;
  gap: 1rem;main.3e16fe79.js:1   POST https://api.planmysemester.xyz/api/subscription/validate-promo-code 400 (Bad Request)
`;

const DisclosureText = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.8125rem;
  text-align: center;
  margin-top: 2rem;
  padding: 1.5rem;
  border-top: 1px solid ${props => props.theme.border};
  line-height: 1.5;
  background: ${props => props.theme.backgroundSecondary};
  border-radius: 8px;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 1rem;
    margin-top: 1.5rem;
  }
`;

const WarningText = styled.div`
  color: ${props => props.theme.statusPending};
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

// Mobile-specific components
const MobileTabs = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid ${props => props.theme.border};
  }
`;

const MobileTab = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  background: ${props => props.active ? props.theme.buttonPrimary : 'transparent'};
  color: ${props => props.active ? '#ffffff' : props.theme.textSecondary};
  border: none;
  border-bottom: 2px solid ${props => props.active ? props.theme.buttonPrimary : 'transparent'};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.theme.text};
    background: ${props => props.active ? props.theme.buttonPrimary : props.theme.background};
  }
`;

// Desktop tab components
const DesktopTabs = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.border};
  justify-content: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopTab = styled.button`
  padding: 0.75rem 2rem;
  background: ${props => props.active ? props.theme.buttonPrimary : 'transparent'};
  color: ${props => props.active ? '#ffffff' : props.theme.textSecondary};
  border: none;
  border-bottom: 2px solid ${props => props.active ? props.theme.buttonPrimary : 'transparent'};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease;
  border-radius: 8px 8px 0 0;

  &:hover {
    color: ${props => props.theme.text};
    background: ${props => props.active ? props.theme.buttonPrimary : props.theme.background};
  }
`;

const MobilePlanCarousel = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: relative;
    overflow: hidden;
    width: 100%;
    touch-action: none; /* Prevent touch scrolling */
    -webkit-overflow-scrolling: touch; /* Disable momentum scrolling on iOS */
  }
`;

const MobilePlanContainer = styled.div`
  display: flex;
  transition: transform 0.3s ease;
  transform: translateX(-${props => props.currentIndex * (100 / props.totalSlides)}%);
  width: ${props => props.totalSlides * 100}%;
`;

const MobilePlanSlide = styled.div`
  width: ${props => 100 / props.totalSlides}%;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0 1rem;
`;

const MobileNavigation = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
  }
`;

const MobileNavButton = styled.button`
  background: ${props => props.theme.buttonPrimary};
  color: ${props => props.theme.text};
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.buttonPrimaryHover};
  }

  &:disabled {
    background: ${props => props.theme.buttonSecondary};
    cursor: not-allowed;
  }
`;

const MobilePlanIndicator = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1rem;
  }
`;

const MobilePlanDot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? props.theme.buttonPrimary : props.theme.border};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.theme.buttonPrimary};
  }
`;

const PaymentFormContainer = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.backgroundSecondary};
`;

const CardElementContainer = styled.div`
  padding: 1rem;
  border: 1px solid ${props => props.theme.inputBorderFocus};
  border-radius: 4px;
  background: ${props => props.theme.inputBackgroundFocus};
  margin: 1rem 0;
`;

// Payment Form Component
const PaymentForm = ({ selectedPlan, selectedPlanTitle, priceIds, onSuccess, onError, isProcessing, promoCode, setPromoCode, promoCodeValid, setPromoCodeValid, promoCodeError, setPromoCodeError, discountedPrice, setDiscountedPrice, selectedPlanPrice, user, promoCodeStatus, setPromoCodeStatus }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState(null);
  const [promoCodeInput, setPromoCodeInput] = useState(promoCode || '');
  const isDevAccount = user?.id === 'dev-admin-user-123';

  // Helper function to extract numeric price from price string
  const parsePrice = (priceString) => {
    if (!priceString) return 0;
    const match = priceString.match(/\$?(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  };

  const numericPrice = parsePrice(selectedPlanPrice);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [promoListOpen, setPromoListOpen] = useState(false);
  const [promoList, setPromoList] = useState([]);
  const [isLoadingPromoList, setIsLoadingPromoList] = useState(false);
  const [promoListError, setPromoListError] = useState('');

  useEffect(() => {
    if (!promoListOpen) return;

    const controller = new AbortController();
    const fetchPromos = async () => {
      setIsLoadingPromoList(true);
      setPromoListError('');

      try {
        const response = await fetch(API_ENDPOINTS.SUBSCRIPTION_PROMOS, {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Unable to load promo codes');
        }

        setPromoList(Array.isArray(data.promos) ? data.promos : []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Failed to fetch promo codes:', error);
          setPromoListError(error.message || 'Unable to load promo codes');
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingPromoList(false);
        }
      }
    };

    fetchPromos();
    return () => controller.abort();
  }, [promoListOpen]);

  const handleSelectPromoCodeFromList = (code) => {
    setPromoCodeInput(code);
    setPromoCode('');
    setPromoCodeValid(false);
    setPromoCodeError('');
    setDiscountedPrice(null);
    setPromoListOpen(false);
  };

  // Validate promo code
  const validatePromoCode = async (code) => {
    if (!code.trim()) {
      setPromoCodeValid(false);
      setPromoCodeError('');
      setPromoCode('');
      setDiscountedPrice(null);
      return;
    }

    setIsValidatingPromo(true);
    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(`${API_BASE_URL}/subscription/validate-promo-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          promoCode: code.trim(),
          priceId: priceIds[selectedPlan]
        })
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (response.ok && data.valid) {
        setPromoCodeValid(true);
        setPromoCodeError('');
        setPromoCode(data.promotionCodeId);
        
        // Calculate discounted price on frontend
        const originalPrice = numericPrice;
        let calculatedDiscountedPrice = originalPrice;
        
        if (data.discountType === 'amount') {
          calculatedDiscountedPrice = Math.max(0, originalPrice - (data.discountAmount / 100));
        } else if (data.discountType === 'percent') {
          calculatedDiscountedPrice = originalPrice * (1 - data.discountAmount / 100);
        }
        
        setDiscountedPrice({
          original: originalPrice,
          discounted: calculatedDiscountedPrice,
          discountDescription: data.discountDescription,
          terms: data.terms
        });
      } else {
        setPromoCodeValid(false);
        setPromoCodeError(data.error || 'Invalid promo code');
        setPromoCode('');
        setDiscountedPrice(null);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setPromoCodeValid(false);
        setPromoCodeError('Request timed out. Please try again.');
        setPromoCode('');
        setDiscountedPrice(null);
      } else {
        setPromoCodeValid(false);
        setPromoCodeError('Failed to validate promo code');
        setPromoCode('');
        setDiscountedPrice(null);
      }
    }
    setIsValidatingPromo(false);
  };

  // Handle promo code input change
  const handlePromoCodeChange = (e) => {
    const value = e.target.value;
    setPromoCodeInput(value);
    if (promoCodeValid && value !== promoCodeInput) {
      setPromoCodeValid(false);
      setPromoCodeError('');
      setPromoCode('');
      setDiscountedPrice(null);
    }
  };

  // Handle promo code apply
  const handleApplyPromoCode = () => {
    validatePromoCode(promoCodeInput);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isDevAccount) {
      // Dev account - skip payment processing
      try {
        const response = await fetch(`${API_BASE_URL}/subscription/create-subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            priceId: priceIds[selectedPlan],
            devMode: true,
            promoCodeId: promoCode || undefined
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to create subscription');
        }

        // Handle promo code status
        if (data.promoCodeError) {
          // Show warning but don't fail the subscription
          console.warn('Promo code application failed:', data.promoCodeError);
          setPromoCodeStatus({ type: 'warning', message: data.promoCodeError });
        } else if (promoCode && data.promoCodeApplied) {
          // Show success message for applied promo code
          setPromoCodeStatus({ type: 'success', message: 'Promo code applied successfully!' });
        }

        onSuccess();
      } catch (error) {
        setPaymentError(error.message);
        onError(error.message);
      }
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setPaymentError(error.message);
        onError(error.message);
        return;
      }

      // Call backend to create subscription
      const response = await fetch(`${API_BASE_URL}/subscription/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          priceId: priceIds[selectedPlan],
          paymentMethodId: paymentMethod.id,
          promoCodeId: promoCode || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create subscription');
      }

      // Handle promo code status
      if (data.promoCodeError) {
        // Show warning but don't fail the subscription
        console.warn('Promo code application failed:', data.promoCodeError);
        setPromoCodeStatus({ type: 'warning', message: data.promoCodeError });
      } else if (promoCode && data.promoCodeApplied) {
        // Show success message for applied promo code
        setPromoCodeStatus({ type: 'success', message: 'Promo code applied successfully!' });
      }

      // Confirm payment intent if needed
      if (data.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
        if (confirmError) {
          throw new Error(confirmError.message);
        }
      }

      onSuccess();

    } catch (error) {
      setPaymentError(error.message);
      onError(error.message);
    }
  };

  return (
    <PaymentFormContainer>
      <h4 style={{ color: 'var(--color-text)', marginBottom: '1rem' }}>Payment Information</h4>
      <form onSubmit={handleSubmit}>
        {/* Promo Code Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          {/* <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <label htmlFor="promo-code" style={{ color: 'var(--color-text)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>Promo Code (Optional)</span>
              <button
                type="button"
                onClick={() => setPromoListOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-button-primary)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '0.875rem',
                  padding: 0
                }}
              >
                View Promos
              </button>
            </label>
          </div> */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input
              id="promo-code"
              type="text"
              value={promoCodeInput}
              onChange={handlePromoCodeChange}
              placeholder="Enter promo code"
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid var(--color-button-primary)',
                borderRadius: '4px',
                background: 'var(--color-background)',
                color: 'var(--color-text)',
                fontSize: '0.875rem'
              }}
              disabled={isProcessing}
            />
            <ElevatedButton
              type="button"
              onClick={handleApplyPromoCode}
              disabled={!promoCodeInput.trim() || isValidatingPromo || isProcessing}
              style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}
            >
              {isValidatingPromo ? 'Validating...' : 'Apply'}
            </ElevatedButton>
          </div>
          {promoCodeValid && (
            <div style={{ color: 'var(--color-button-success)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
              ✓ Promo code applied: {discountedPrice?.discountDescription}
              {discountedPrice?.terms && (
                <div style={{ marginTop: '0.25rem', fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                  Terms: {discountedPrice.terms}
                </div>
              )}
            </div>
          )}
          {promoCodeError && (
            <div style={{ color: '#fa755a', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
              {promoCodeError}
            </div>
          )}
          {promoCodeStatus && (
            <div style={{
              color: promoCodeStatus.type === 'success' ? 'var(--color-button-success)' : 'var(--color-status-pending)',
              fontSize: '0.875rem',
              marginBottom: '0.5rem',
              padding: '0.5rem',
              borderRadius: '4px',
              background: promoCodeStatus.type === 'success' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(255, 165, 0, 0.1)',
              border: `1px solid ${promoCodeStatus.type === 'success' ? 'var(--color-button-success)' : 'var(--color-status-pending)'}`
            }}>
              {promoCodeStatus.type === 'success' ? '✓' : '⚠'} {promoCodeStatus.message}
            </div>
          )}

          <Modal isOpen={promoListOpen} onClose={() => setPromoListOpen(false)} title="Available Promo Codes" size="medium">
            <div style={{ minWidth: '320px', maxWidth: '100%', padding: '0.25rem' }}>
              {isLoadingPromoList ? (
                <p style={{ color: 'var(--color-text)', margin: 0 }}>Loading current promotions…</p>
              ) : promoListError ? (
                <p style={{ color: '#fa755a', margin: 0 }}>{promoListError}</p>
              ) : promoList.length === 0 ? (
                <p style={{ color: 'var(--color-text)', margin: 0 }}>No active promo codes are available right now.</p>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {promoList.map((promo) => (
                    <div key={promo.id} style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '8px', background: 'var(--color-background)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>{promo.code}</div>
                          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>{promo.discountDescription}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleSelectPromoCodeFromList(promo.code)}
                          style={{
                            border: '1px solid var(--color-button-primary)',
                            background: 'var(--color-button-primary)',
                            color: '#fff',
                            borderRadius: '4px',
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          Use Code
                        </button>
                      </div>
                      <div style={{ marginTop: '0.75rem', color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
                        {promo.durationText && <div>{promo.durationText}</div>}
                        {promo.firstTimeTransaction && <div>New customers only</div>}
                        {promo.redeemBy && <div>Expires: {new Date(promo.redeemBy * 1000).toLocaleDateString()}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Modal>
        </div>

        {/* Price Summary */}
        <div style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '8px', background: 'var(--color-background-secondary)' }}>
          <h5 style={{ color: 'var(--color-text)', margin: '0 0 1rem 0', fontSize: '1rem' }}>Order Summary</h5>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ color: 'var(--color-text-muted)' }}>{selectedPlanTitle}</span>
            <span style={{ color: 'var(--color-text)' }}>${numericPrice.toFixed(2)}</span>
          </div>
          {promoCodeValid && discountedPrice && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: 'var(--color-button-success)' }}>Discount ({discountedPrice.discountDescription})</span>
                <span style={{ color: 'var(--color-button-success)' }}>-${(discountedPrice.original - discountedPrice.discounted).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-border)' }}>
                <span style={{ color: 'var(--color-text)', fontWeight: '600' }}>Total</span>
                <span style={{ color: 'var(--color-text)', fontWeight: '600' }}>${discountedPrice.discounted.toFixed(2)}</span>
              </div>
            </>
          )}
          {!promoCodeValid && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-border)' }}>
              <span style={{ color: 'var(--color-text)', fontWeight: '600' }}>Total</span>
              <span style={{ color: 'var(--color-text)', fontWeight: '600' }}>${numericPrice.toFixed(2)}</span>
            </div>
          )}
        </div>

        {!isDevAccount && (
          <CardElementContainer>
            <label htmlFor="card-element" style={{ display: 'block', color: 'var(--color-text)', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Card Information
            </label>
            <CardElement
              id="card-element"
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: 'var(--color-text)',
                    '::placeholder': {
                      color: 'var(--color-text-muted)',
                    },
                  },
                  invalid: {
                    color: '#fa755a',
                  },
                },
              }}
            />
          </CardElementContainer>
        )}
        {paymentError && (
          <div style={{ color: '#fa755a', marginBottom: '1rem' }}>
            {paymentError}
          </div>
        )}
        <ElevatedButton
          type="submit"
          disabled={(!stripe && !isDevAccount) || isProcessing}
          style={{ width: '100%' }}
        >
          {isProcessing ? 'Processing Payment...' : isDevAccount ? `Activate ${selectedPlanTitle} (Dev Mode)` : `Subscribe to ${selectedPlanTitle}`}
        </ElevatedButton>
      </form>
    </PaymentFormContainer>
  );
};

const BestValueBadge = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.statusPending};
  color: ${props => props.theme.text};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 10;

  @media (max-width: 768px) {
    top: -5px;
    padding: 0.2rem 0.5rem;
    font-size: 0.65rem;
    border-radius: 8px;
  }
`;

const ScrollableContent = styled.div`
  max-height: calc(85vh - 120px); /* Account for header and footer */
  padding-right: 0.5rem;
  overflow-y: auto;

  @media (max-width: 768px) {
    max-height: calc(90vh - 100px); /* Adjust for mobile */
  }

  /* Inner scrollbar removed - only outer modal scroll remains */
`;

const SubscriptionModal = ({ isOpen, onClose, showOnlyAI = false }) => {
  const { user, updateSubscription } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanObject, setSelectedPlanObject] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dynamicPlans, setDynamicPlans] = useState(null);
  const [dynamicAIPlans, setDynamicAIPlans] = useState(null);
  const [dynamicPriceIds, setDynamicPriceIds] = useState({});
  const [taxCalculation, setTaxCalculation] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeValid, setPromoCodeValid] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [promoCodeStatus, setPromoCodeStatus] = useState(null); // { type: 'success'|'warning', message: string }
  const [tab, setTab] = useState(showOnlyAI ? 'ai' : 'premium'); // 'premium' or 'ai' for both mobile and desktop
  const [mobilePlanIndex, setMobilePlanIndex] = useState(0); // Index for mobile carousel


  const isDevAccount = user?.id === 'dev-admin-user-123';

  // Reset promo code state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPromoCode('');
      setPromoCodeValid(false);
      setPromoCodeError('');
      setDiscountedPrice(null);
      setPromoCodeStatus(null);
    }
  }, [isOpen]);

  // Get user's current plan
  const getCurrentPlan = () => {
    // Check expiration mathematically
    const isExpired = user?.subscriptionEndDate ? (() => {
      const endDate = new Date(user.subscriptionEndDate);
      endDate.setHours(12, 0, 0, 0); // Set to noon
      return new Date() >= endDate;
    })() : false;

    // Check cancellation statuses mapping
    const status = user?.subscriptionStatus;
    const isCancelled = ['canceled', 'cancelled', 'past_due', 'unpaid', 'incomplete_expired', 'incomplete'].includes(status);

    // If they are expired or gracefully cancelled, they have NO current plan
    if (isExpired || isCancelled || user?.cancelAtPeriodEnd) {
      return null;
    }

    // Check AI tier first (ai_light, ai_moderate, ai_pro, ai_max)
    if (user?.aiTier && ['ai_light', 'ai_moderate', 'ai_pro', 'ai_max'].includes(user.aiTier)) {
      return user.aiTier;
    }
    // Check regular subscription
    if (user?.subscription && user.subscription !== 'free' && user.subscription !== 'basic') {
      return user.subscription;
    }
    return null;
  };

  const currentPlan = getCurrentPlan();

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    // Don't allow selecting the current plan
    if (plan.id === currentPlan) {
      return;
    }
    setSelectedPlan(plan.id);
    setSelectedPlanObject(plan);
    // Reset promo code when plan changes
    setPromoCode('');
    setPromoCodeValid(false);
    setPromoCodeError('');
    setDiscountedPrice(null);
    setPromoCodeStatus(null);
  };

  // Handle tab change (for both mobile and desktop)
  const handleTabChange = (newTab) => {
    setTab(newTab);
    setMobilePlanIndex(0); // Reset to first plan in new tab
  };

  // Handle mobile plan navigation
  const handleMobilePlanChange = (direction) => {
    const currentPlans = tab === 'premium' ? plans.slice(0, 3) : plans.slice(3);
    const maxIndex = currentPlans.length - 1;
    
    if (direction === 'next' && mobilePlanIndex < maxIndex) {
      setMobilePlanIndex(mobilePlanIndex + 1);
    } else if (direction === 'prev' && mobilePlanIndex > 0) {
      setMobilePlanIndex(mobilePlanIndex - 1);
    }
  };

  // Fetch pricing plans from Stripe on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plansData = await getPricingPlans();
        const { regularPlansData, aiPlansData } = splitPricingPlans(plansData);

        setDynamicPlans(regularPlansData);
        setDynamicAIPlans(aiPlansData);

        setDynamicPriceIds({
          monthly: regularPlansData.monthly?.priceId || '',
          quarterly: regularPlansData.quarterly?.priceId || '',
          yearly: regularPlansData.yearly?.priceId || '',
          ai_light: aiPlansData.ai_light?.priceId || '',
          ai_moderate: aiPlansData.ai_moderate?.priceId || '',
          ai_pro: aiPlansData.ai_pro?.priceId || '',
          ai_max: aiPlansData.ai_max?.priceId || ''
        });
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      }
    };

    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen]);

  // Fallback price IDs if API fails - wrapped in useMemo to prevent dependency changes
  const priceIds = useMemo(() => ({
    monthly: dynamicPriceIds.monthly || 'price_1SHanwEfX9gH4otvOhYTQk50',
    quarterly: dynamicPriceIds.quarterly || 'price_1SHamcEfX9gH4otvLrSvXJhI',
    yearly: dynamicPriceIds.yearly || 'price_1Sbr7DEfX9gH4otvMRWi9xo6',
    ai_light: dynamicPriceIds.ai_light || 'price_1ScbOZEfX9gH4otvNNbJNyOc',
    ai_moderate: dynamicPriceIds.ai_moderate || 'price_1ScbP2EfX9gH4otv07jIKr6T',
    ai_pro: dynamicPriceIds.ai_pro || 'price_1ScbPOEfX9gH4otvJYrgxBQp',
    ai_max: dynamicPriceIds.ai_max || 'price_1ScbPvEfX9gH4otvF4lraxhe'
  }), [dynamicPriceIds.monthly, dynamicPriceIds.quarterly, dynamicPriceIds.yearly, dynamicPriceIds.ai_light, dynamicPriceIds.ai_moderate, dynamicPriceIds.ai_pro, dynamicPriceIds.ai_max]);

  // Fetch tax calculation when selected plan changes
  useEffect(() => {
    const calculateTax = async () => {
      if (!selectedPlan || !priceIds[selectedPlan] || isDevAccount) {
        setTaxCalculation(null);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/subscription/calculate-total`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ priceId: priceIds[selectedPlan] })
        });

        if (response.ok) {
          const taxData = await response.json();
          setTaxCalculation(taxData);
        } else {
          setTaxCalculation(null);
        }
      } catch (error) {
        console.error('Failed to calculate tax:', error);
        setTaxCalculation(null);
      }
    };

    if (isOpen && selectedPlan && priceIds[selectedPlan]) {
      calculateTax();
    }
  }, [selectedPlan, isOpen, priceIds, isDevAccount]);

  // Detect user location for region-specific policies
  useEffect(() => {
    const detectLocation = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.PUBLIC_GEO, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        
        // EU country codes
        const euCountries = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 
                            'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 
                            'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB'];
        
        const isEU = euCountries.includes(data.countryCode);
        setUserLocation(isEU ? 'EU' : 'US');
      } catch (error) {
        console.error('Location detection failed:', error);
        setUserLocation('US'); // Default to US if detection fails
      }
    };

    if (isOpen && !userLocation) {
      detectLocation();
    }
  }, [isOpen, userLocation]);

  // Function to format price display
  const formatPrice = (amount, interval, intervalCount) => {
    if (!amount) return '$0.00';
    
    const price = `$${amount.toFixed(2)}`;
    
    if (interval === 'month' && intervalCount === 1) {
      return `${price}`;
    } else if (interval === 'month' && intervalCount === 3) {
      return `${price}`;
    } else if (interval === 'year' && intervalCount === 1) {
      return `${price}`;
    } else if (interval === 'day' && intervalCount === 30) {
      return `${price}`;
    } else if (interval === 'day' && intervalCount === 90) {
      return `${price}`;
    } else if (interval === 'day' && intervalCount === 365) {
      return `${price}`;
    }
    
    return price;
  };

  // Function to calculate savings and descriptions
  const getPlanDetails = (planType, amount) => {
    
    
    if (!amount || !dynamicPlans) {
      
      // Fallback to hardcoded values
      switch (planType) {
        case 'monthly':
          return {
            price: '$2.99',
            priceDescriptions: 'Standard pricing',
            period: '$2.99 billed every month.'
          };
        case 'quarterly':
          return {
            price: '$6.99',
            priceDescriptions: 'Save 22% compared to monthly',
            period: '$6.99 billed every 3 months (equivalent to $2.33/month, saves 22% vs monthly).'
          };
        case 'yearly':
          return {
            price: '$24.99',
            priceDescriptions: 'Save 30% compared to monthly',
            period: '$24.99 billed every year (equivalent to $2.08/month, saves 30% vs monthly).'
          };
        default:
          return {
            price: '$0.00',
            priceDescriptions: 'Price not available',
            period: 'Contact support for pricing.'
          };
      }
    }

    const monthlyPrice = dynamicPlans.monthly?.amount || 4.99;
    const currentPrice = amount;
    
    let savings = 0;
    let savingsText = '';
    
    if (planType === 'quarterly') {
      const monthlyEquivalent = currentPrice / 3;
      savings = ((monthlyPrice - monthlyEquivalent) / monthlyPrice) * 100;
      savingsText = `Save ${Math.round(savings)}% compared to monthly`;
    } else if (planType === 'yearly') {
      const monthlyEquivalent = currentPrice / 12;
      savings = ((monthlyPrice - monthlyEquivalent) / monthlyPrice) * 100;
      savingsText = `Save ${Math.round(savings)}% compared to monthly`;
    } else {
      savingsText = 'Standard pricing';
    }

    const interval = dynamicPlans[planType]?.interval;
    const intervalCount = dynamicPlans[planType]?.intervalCount;
    
    let periodText = '';
    if (interval === 'month' && intervalCount === 1) {
      periodText = `$${currentPrice.toFixed(2)} billed every month.`;
    } else if (interval === 'month' && intervalCount === 3) {
      const monthlyEquivalent = currentPrice / 3;
      periodText = `$${currentPrice.toFixed(2)} billed every 3 months (equivalent to $${monthlyEquivalent.toFixed(2)}/month, saves ${Math.round(savings)}% vs monthly).`;
    } else if (interval === 'year' && intervalCount === 1) {
      const monthlyEquivalent = currentPrice / 12;
      periodText = `$${currentPrice.toFixed(2)} billed every year (equivalent to $${monthlyEquivalent.toFixed(2)}/month, saves ${Math.round(savings)}% vs monthly).`;
    } else if (interval === 'day' && intervalCount === 30) {
      periodText = `$${currentPrice.toFixed(2)} billed every 30 days.`;
    } else if (interval === 'day' && intervalCount === 90) {
      const monthlyEquivalent = currentPrice / 3;
      periodText = `$${currentPrice.toFixed(2)} billed every 90 days (equivalent to $${monthlyEquivalent.toFixed(2)}/month, saves ${Math.round(savings)}% vs monthly).`;
    } else if (interval === 'day' && intervalCount === 365) {
      const monthlyEquivalent = currentPrice / 12;
      periodText = `$${currentPrice.toFixed(2)} billed every 365 days (equivalent to $${monthlyEquivalent.toFixed(2)}/month, saves ${Math.round(savings)}% vs monthly).`;
    }

    return {
      price: formatPrice(currentPrice, interval, intervalCount),
      priceDescriptions: savingsText,
      period: periodText
    };
  };

  // Dynamic refund policy based on user location
  const refundPolicy = userLocation === 'EU' ? 
    "*14-day cancellation with full refund available for EU customers." : 
    "*There are no refunds.";

  const euDisclosure = userLocation === 'EU' ? (
    <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
      <strong>EU Consumer Rights:</strong><br />
      You have the right to cancel this subscription within 14 days of purchase for a full refund.<br />
      For digital content, this right applies even after access has been granted.<br />
      To cancel, contact support within the 14-day period.
    </div>
  ) : null;

  // Function to calculate AI value per credit ratio
  const getAIValueRatio = (planId) => {
    const aiPlans = [
      { id: 'ai_light', price: dynamicAIPlans?.ai_light?.amount || 7.99, tokens: 250000 },
      { id: 'ai_moderate', price: dynamicAIPlans?.ai_moderate?.amount || 9.99, tokens: 500000 },
      { id: 'ai_pro', price: dynamicAIPlans?.ai_pro?.amount || 12.99, tokens: 1000000 },
      { id: 'ai_max', price: dynamicAIPlans?.ai_max?.amount || 16.99, tokens: 1500000 }
    ];

    // Calculate cost per 1,000 tokens for each plan
    const costPerThousandTokens = aiPlans.map(plan => ({
      ...plan,
      costPerThousand: (plan.price / (plan.tokens / 1000))
    }));

    // Find the highest cost per 1,000 tokens (baseline for comparison)
    const maxCostPerThousand = Math.max(...costPerThousandTokens.map(p => p.costPerThousand));
    
    // Find current plan
    const currentPlan = costPerThousandTokens.find(p => p.id === planId);
    if (!currentPlan) return '';

    // Calculate savings percentage
    const savingsPercent = ((maxCostPerThousand - currentPlan.costPerThousand) / maxCostPerThousand) * 100;
    
    if (savingsPercent >= 60) return `saves ${Math.round(savingsPercent)}% value per credit ratio`;
    if (savingsPercent >= 35) return `saves ${Math.round(savingsPercent)}% value per credit ratio`;
    if (savingsPercent >= 15) return `saves ${Math.round(savingsPercent)}% value per credit ratio`;
    return `saves ${Math.round(savingsPercent)}% value per credit ratio`;
  };

  const plans = [
    // Regular Premium Plans (shown first)
    {
      id: 'monthly',
      title: 'Monthly Plan',
      ...getPlanDetails('monthly', dynamicPlans?.monthly?.amount),
      // tokens: '200,000 tokens/month',
      features: [
        'Everything in Free',
        'Multi-semester planning',
        'Unlimited Flashcard decks',
        'Unlimited Flashcards per deck',
        // 'Enhanced AI Study guides',
        // 'Enhanced AI Flashcard generation',
        // 'Enhanced AI Syllabus analysis',
        // 'AI Learning Summary (enhanced)',
        // 'AI Flashcards (premium decks)',
        // '200,000 AI tokens per month',
        'Access to all future premium features',
        // 'Flash Lite (Fast)',
        'File uploads supported for notes (50MB max)'
      ],
      highlight2: true
    },
    {
      id: 'quarterly',
      title: 'Quarterly Plan',
      ...getPlanDetails('quarterly', dynamicPlans?.quarterly?.amount),
      // tokens: '200,000 tokens/month',
      features: [
        'Everything in Free',
        'Multi-semester planning',
        'Unlimited Flashcard decks',
        'Unlimited Flashcards per deck',
        // 'Enhanced AI Study guides',
        // 'Enhanced AI Flashcard generation',
        // 'Enhanced AI Syllabus analysis',
        // 'AI Learning Summary (enhanced)',
        // 'AI Flashcards (premium decks)',
        // '200,000 AI tokens per month',
        'Access to all future premium features',
        // 'Flash Lite (Fast)',
        'File uploads supported for notes (50MB max)'
      ],
      highlight3: true
    },
    {
      id: 'yearly',
      title: 'Yearly Plan',
      ...getPlanDetails('yearly', dynamicPlans?.yearly?.amount),
      // tokens: '200,000 tokens/month',
      features: [
        'Everything in Free',
        'Multi-semester planning',
        'Unlimited Flashcard decks',
        'Unlimited Flashcards per deck',
        // 'Enhanced AI Study guides',
        // 'Enhanced AI Flashcard generation',
        // 'Enhanced AI Syllabus analysis',
        // 'AI Learning Summary (enhanced)',
        // 'AI Flashcards (premium decks)',
        // '200,000 AI tokens per month',
        'Access to all future premium features',
        // 'Flash Lite (Fast)',
        'File uploads supported for notes (50MB max)'
      ],
      highlight: true
    },
    // AI Plans (shown below Premium plans)
    {
      id: 'ai_max',
      title: 'AI Max',
      price: dynamicAIPlans?.ai_max ? `$${(dynamicAIPlans.ai_max.amount || 16.99).toFixed(2)}` : '$16.99',
      period: `$${dynamicAIPlans?.ai_max?.amount?.toFixed(2) || '16.99'} billed every 30 days (${getAIValueRatio('ai_max')}).`,
      tokens: '2,250,000 tokens/month',
      features: [
        'Includes all premium features',
        'Advanced syllabi analysis',
        'AI Tutor for assignments',
        'Advanced study guides',
        'AI Flashcard Study Decks',
        'Advanced flashcard generation',
        'AI Learning Summary (advanced)',
        '2,250,000 AI tokens per month',
        'Choose from all AI models',
        'File uploads supported for AI chats and notes (100MB max)'
      ],
      highlight: true,
      isAI: true
    },
    {
      id: 'ai_light',
      title: 'AI Light',
      price: dynamicAIPlans?.ai_light ? `$${(dynamicAIPlans.ai_light.amount || 7.99).toFixed(2)}` : '$7.99',
      period: `$${dynamicAIPlans?.ai_light?.amount?.toFixed(2) || '7.99'} billed every 30 days (${getAIValueRatio('ai_light')}).`,
      tokens: '500,000 tokens/month',
      features: [
        'Includes all premium features',
        'Advanced syllabi analysis',
        'AI Tutor for assignments',
        'Advanced study guides',
        'AI Flashcard Study Decks',
        'Advanced flashcard generation',
        'AI Learning Summary (advanced)',
        '500,000 AI tokens per month',
        'Choose from all AI models',
        'File uploads supported for AI chats and notes (100MB max)'
      ],
      highlight2: true,
      isAI: true
    },
    {
      id: 'ai_moderate',
      title: 'AI Moderate',
      price: dynamicAIPlans?.ai_moderate ? `$${(dynamicAIPlans.ai_moderate.amount || 9.99).toFixed(2)}` : '$9.99',
      period: `$${dynamicAIPlans?.ai_moderate?.amount?.toFixed(2) || '9.99'} billed every 30 days (${getAIValueRatio('ai_moderate')}).`,
      tokens: '1,000,000 tokens/month',
      features: [
        'Includes all premium features',
        'Advanced syllabi analysis',
        'AI Tutor for assignments',
        'Advanced study guides',
        'AI Flashcard Study Decks',
        'Advanced flashcard generation',
        'AI Learning Summary (advanced)',
        '1,000,000 AI tokens per month',
        'Choose from all AI models',
        'File uploads supported for AI chats and notes (100MB max)'
      ],
      highlight3: true,
      isAI: true
    },
    {
      id: 'ai_pro',
      title: 'AI Pro',
      price: dynamicAIPlans?.ai_pro ? `$${(dynamicAIPlans.ai_pro.amount || 12.99).toFixed(2)}` : '$12.99',
      period: `$${dynamicAIPlans?.ai_pro?.amount?.toFixed(2) || '12.99'} billed every 30 days (${getAIValueRatio('ai_pro')}).`,
      tokens: '1,750,000 tokens/month',
      features: [
        'Includes all premium features',
        'Advanced syllabi analysis',
        'AI Tutor for assignments',
        'Advanced study guides',
        'AI Flashcard Study Decks',
        'Advanced flashcard generation',
        'AI Learning Summary (advanced)',
        '1,750,000 AI tokens per month',
        'Choose from all AI models',
        'File uploads supported for AI chats and notes (100MB max)'
      ],
      highlight4: true,
      isAI: true
    }
  ];

  const handleSubscribe = async () => {
    setIsProcessing(true);

    if (isDevAccount) {
      // Dev account bypass - directly update subscription
      const selectedPlanData = plans.find(plan => plan.id === selectedPlan);
      if (selectedPlanData?.isAI) {
        // For AI plans, update both subscription and aiTier
        updateSubscription('monthly', selectedPlan);
      } else {
        // For regular plans, update subscription
        updateSubscription(selectedPlan);
      }
      onClose();
      setIsProcessing(false);
      return;
    }

    // Check if this is an AI plan
    const selectedPlanData = plans.find(plan => plan.id === selectedPlan);
    if (selectedPlanData?.isAI) {
      // AI plans use Stripe checkout (redirect)
      try {
        const response = await fetch(API_ENDPOINTS.AI_CREATE_SUBSCRIPTION, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ priceId: dynamicPriceIds[selectedPlan] })
        });

        if (response.ok) {
          const result = await response.json();
          // Redirect to Stripe checkout
          window.location.href = result.checkoutUrl;
        } else {
          const error = await response.json();
          alert(`Subscription failed: ${error.message}`);
          setIsProcessing(false);
        }
      } catch (error) {
        console.error('AI subscription error:', error);
        alert('Failed to create subscription. Please try again.');
        setIsProcessing(false);
      }
      return;
    }

    // Regular plans use inline payment form
    // Payment processing is now handled in PaymentForm component
    // This function is kept for backward compatibility but payment is handled via PaymentForm
  };

  const handlePaymentSuccess = () => {
    updateSubscription(selectedPlan);
    onClose();
    setIsProcessing(false);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setIsProcessing(false);
    // Error is already displayed in PaymentForm
  };

  return (
    
    <Modal isOpen={isOpen} onClose={onClose} title={currentPlan ? "Upgrade or Change Plan" : "Premium & AI Plans"}>
      <ScrollableContent>
        {!selectedPlan ? (
          <>
            <div style={{ color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
              {currentPlan 
                ? "Choose a different plan to upgrade or downgrade your subscription. Your current plan is grayed out."
                : window.innerWidth <= 768
                  ? `Choose a ${tab === 'premium' ? 'premium' : 'AI'} plan for unlimited academic features${tab === 'ai' ? ' plus AI-powered study tools' : ''}.`
                  : `Choose a ${tab === 'premium' ? 'premium' : 'AI'} plan for ${tab === 'premium' ? 'additional upload capacity and benefits' : 'advanced AI-powered study tools'}.`
              }
            </div>

            {/* Mobile Tabs */}
            {!showOnlyAI && (
              <MobileTabs>
                <MobileTab 
                  active={tab === 'premium'} 
                  onClick={() => handleTabChange('premium')}
                >
                  Premium Plans
                </MobileTab>
                {/* <MobileTab 
                  active={tab === 'ai'} 
                  onClick={() => handleTabChange('ai')}
                >
                  AI Plans
                </MobileTab> */}
              </MobileTabs>
            )}

            {/* Desktop Tabs */}
            {!showOnlyAI && (
              <DesktopTabs>
                <DesktopTab 
                  active={tab === 'premium'} 
                  onClick={() => handleTabChange('premium')}
                >
                  Premium Plans
                </DesktopTab>
                {/* <DesktopTab 
                  active={tab === 'ai'} 
                  onClick={() => handleTabChange('ai')}
                >
                  AI Plans
                </DesktopTab> */}
              </DesktopTabs>
            )}

            {/* Desktop Grid Layout */}
            <div style={{ 
              display: window.innerWidth <= 768 ? 'none' : 'block',
              marginTop: '1.5rem', 
              maxWidth: '900px', 
              marginLeft: 'auto', 
              marginRight: 'auto' 
            }}>
              {/* Plans based on selected tab */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '1rem', 
                justifyItems: 'stretch',
                maxWidth: '900px',
                margin: '0 auto'
              }}>
                {(tab === 'premium' ? plans.slice(0, 3) : plans.slice(3)).map(plan => (
                  <SubscriptionCard
                    key={plan.id}
                    selected={selectedPlan === plan.id}
                    highlight={!!plan.highlight}
                    disabled={plan.id === currentPlan}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    {plan.id === currentPlan && <BestValueBadge>Current Plan</BestValueBadge>}
                    {plan.id !== currentPlan && plan.highlight && <BestValueBadge>Best Value</BestValueBadge>}
                    {/* {plan.id !== currentPlan && plan.highlight2 && <BestValueBadge>Limited Risk</BestValueBadge>}
                    {plan.id !== currentPlan && plan.highlight3 && <BestValueBadge>Most Flexible</BestValueBadge>} */}
                    {/* {plan.id !== currentPlan && plan.highlight4 && <BestValueBadge>Best all around</BestValueBadge>} */}
                    <PlanTitle>{plan.title}</PlanTitle>
                    
                    {/* Show base price with strikethrough if tax applies and this plan is selected */}
                    {taxCalculation && taxCalculation.tax > 0 && selectedPlan === plan.id && !isDevAccount ? (
                      <>
                        <PlanTotalPrice hasTax={true}>{plan.price}</PlanTotalPrice>
                        <PlanTaxIncluded>${taxCalculation?.total?.toFixed(2) || '0.00'} total</PlanTaxIncluded>
                      </>
                    ) : (
                      <PlanPrice>{plan.price}</PlanPrice>
                    )}
                    
                    {/* Show AI tokens in blue for AI plans */}
                    {plan.tokens && (
                      <div style={{ color: 'var(--color-button-primary)', fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: '600' }}>
                        {plan.tokens}
                      </div>
                    )}
                    
                    <div style={{ color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>{plan.priceDescriptions}</div>
                    <PlanFeatures>
                      {plan.features.map((feature, index) => (
                        <PlanFeature key={index}>{feature}</PlanFeature>
                      ))}
                    </PlanFeatures>
                    <br />
                    <PlanPeriod>{plan.period}</PlanPeriod>
                  </SubscriptionCard>
                ))}
              </div>
            </div>

            <div style={{ 
              background: 'var(--color-background)', 
              border: '1px solid var(--color-button-primary)', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginTop: '1.5rem',
              fontSize: '0.9rem'
            }}>
              <div style={{ color: 'var(--color-text)', fontWeight: '600', marginBottom: '0.5rem' }}>
                Can't Afford a Paid Plan?
              </div>
              <div style={{ color: 'var(--color-text-muted)', lineHeight: '1.4' }}>
                <p style={{ fontWeight: '500' }}>
                  We believe financial constraints shouldn't hinder access to quality educational tools. If you are a student, educator, or someone in financial <br/>need, please reach out via our support system. We offer personalized, 
                  discounted plans that feature the exact same benefits as full-price <br />subscriptions.
                </p>
              </div>
            </div>

            {/* Mobile Plan Carousel */}
            <MobilePlanCarousel>
              {(() => {
                const currentPlans = tab === 'premium' ? plans.slice(0, 3) : plans.slice(3);
                return (
                  <MobilePlanContainer currentIndex={mobilePlanIndex} totalSlides={currentPlans.length}>
                    {currentPlans.map((plan, index) => (
                      <MobilePlanSlide key={plan.id} totalSlides={currentPlans.length}>
                        <SubscriptionCard
                          selected={selectedPlan === plan.id}
                          highlight={!!plan.highlight}
                          disabled={plan.id === currentPlan}
                          onClick={() => handlePlanSelect(plan)}
                          style={{ width: '100%' }}
                        >
                          {plan.id === currentPlan && <BestValueBadge>Current Plan</BestValueBadge>}
                          {plan.id !== currentPlan && plan.highlight && <BestValueBadge>Best Value</BestValueBadge>}
                          {/* {plan.id !== currentPlan && plan.highlight2 && <BestValueBadge>Limited Risk</BestValueBadge>}
                          {plan.id !== currentPlan && plan.highlight3 && <BestValueBadge>Most Flexible</BestValueBadge>} */}
                          {/* {plan.id !== currentPlan && plan.highlight4 && <BestValueBadge>Best all around</BestValueBadge>} */}
                          <PlanTitle>{plan.title}</PlanTitle>
                          
                          {/* Show base price with strikethrough if tax applies and this plan is selected */}
                          {taxCalculation && taxCalculation.tax > 0 && selectedPlan === plan.id && !isDevAccount ? (
                            <>
                              <PlanTotalPrice hasTax={true}>{plan.price}</PlanTotalPrice>
                              <PlanTaxIncluded>${taxCalculation?.total?.toFixed(2) || '0.00'} total</PlanTaxIncluded>
                            </>
                          ) : (
                            <PlanPrice>{plan.price}</PlanPrice>
                          )}
                          
                          {/* Show AI tokens in blue for AI plans */}
                          {plan.tokens && (
                            <div style={{ color: 'var(--color-button-primary)', fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: '600' }}>
                              {plan.tokens}
                            </div>
                          )}
                          
                          <div style={{ color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>{plan.priceDescriptions}</div>
                          <PlanFeatures>
                            {plan.features.map((feature, index) => (
                              <PlanFeature key={index}>{feature}</PlanFeature>
                            ))}
                          </PlanFeatures>
                          <br />
                          <PlanPeriod>{plan.period}</PlanPeriod>
                        </SubscriptionCard>
                      </MobilePlanSlide>
                    ))}
                  </MobilePlanContainer>
                );
              })()}

              {/* Mobile Navigation */}
              <MobileNavigation>
                <MobileNavButton 
                  onClick={() => handleMobilePlanChange('prev')}
                  disabled={mobilePlanIndex === 0}
                >
                  ‹
                </MobileNavButton>
                
                <div style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                  {mobilePlanIndex + 1} of {(tab === 'premium' ? plans.slice(0, 3) : plans.slice(3)).length}
                </div>
                
                <MobileNavButton 
                  onClick={() => handleMobilePlanChange('next')}
                  disabled={mobilePlanIndex >= (tab === 'premium' ? plans.slice(0, 3) : plans.slice(3)).length - 1}
                >
                  ›
                </MobileNavButton>
              </MobileNavigation>

              {/* Mobile Plan Indicators */}
              <MobilePlanIndicator>
                {(tab === 'premium' ? plans.slice(0, 3) : plans.slice(3)).map((_, index) => (
                  <MobilePlanDot
                    key={index}
                    active={index === mobilePlanIndex}
                    onClick={() => setMobilePlanIndex(index)}
                  />
                ))}
              </MobilePlanIndicator>
            </MobilePlanCarousel>

            <ButtonContainer>
              <ElevatedButton
                onClick={onClose}
                style={{ background: 'var(--color-button-secondary)' }}
              >
                Cancel
              </ElevatedButton>
            </ButtonContainer>
          </>
        ) : (
          <PaymentSection>
            <BackButton onClick={() => { setSelectedPlan(null); setSelectedPlanObject(null); }}>← Back to Plans</BackButton>
            <PlanSummary>
              <SelectedPlanTitle>Subscribe to {selectedPlanObject?.title || selectedPlan}</SelectedPlanTitle>
              <PlanDetails>
                <DetailItem>{selectedPlanObject?.tokens || 'Premium Features'}</DetailItem>
                <DetailItem>Monthly subscription</DetailItem>
                <DetailItem>Cancel anytime</DetailItem>
              </PlanDetails>
            </PlanSummary>

            {taxCalculation && !isDevAccount && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: 'var(--color-background)',
                borderRadius: '8px',
                border: '1px solid var(--color-button-primary)'
              }}>
                <div style={{ color: 'var(--color-text)', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Order Summary
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>
                  <span>Subtotal:</span>
                  <span>${taxCalculation.subtotal.toFixed(2)}</span>
                </div>
                {taxCalculation.tax > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>
                    <span>Tax ({taxCalculation.taxPercent}%):</span>
                    <span>${taxCalculation.tax.toFixed(2)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'var(--color-text)',
                  fontWeight: '700',
                  fontSize: '1.125rem',
                  marginTop: '0.5rem',
                  paddingTop: '0.5rem',
                  borderTop: '1px solid var(--color-border)'
                }}>
                  <span>Total:</span>
                  <span>${taxCalculation?.total?.toFixed(2) || '0.00'}</span>
                </div>
                <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Tax calculated based on your location. Final amount guaranteed at checkout.
                </div>
              </div>
            )}

            <Elements stripe={stripePromise}>
              <PaymentForm
                selectedPlan={selectedPlan}
                selectedPlanTitle={selectedPlanObject?.title || selectedPlan}
                priceIds={priceIds}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                isProcessing={isProcessing}
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                promoCodeValid={promoCodeValid}
                setPromoCodeValid={setPromoCodeValid}
                promoCodeError={promoCodeError}
                setPromoCodeError={setPromoCodeError}
                discountedPrice={discountedPrice}
                setDiscountedPrice={setDiscountedPrice}
                selectedPlanPrice={selectedPlanObject?.price}
                user={user}
                promoCodeStatus={promoCodeStatus}
                setPromoCodeStatus={setPromoCodeStatus}
              />
            </Elements>

            {isDevAccount && (
              <>
                {taxCalculation && (
                  <div style={{
                    marginTop: '1.5rem',
                    padding: '1rem',
                    background: 'var(--color-background)',
                    borderRadius: '8px',
                    border: '1px solid var(--color-button-primary)'
                  }}>
                    <div style={{ color: 'var(--color-text)', fontWeight: '600', marginBottom: '0.5rem' }}>
                      Order Summary (Dev Mode - Free)
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>
                      <span>Subtotal:</span>
                      <span>$0.00 (Dev Mode)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>
                      <span>Tax:</span>
                      <span>$0.00 (Dev Mode)</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: 'var(--color-text)',
                      fontWeight: '700',
                      fontSize: '1.125rem',
                      marginTop: '0.5rem',
                      paddingTop: '0.5rem',
                      borderTop: '1px solid var(--color-border)'
                    }}>
                      <span>Total:</span>
                      <span>$0.00 (Dev Mode)</span>
                    </div>
                  </div>
                )}

                <ButtonContainer>
                  <ElevatedButton onClick={onClose} variant="secondary">
                    Cancel
                  </ElevatedButton>
                  <ElevatedButton
                    onClick={handleSubscribe}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Processing...' : `Activate ${selectedPlanObject?.title || selectedPlan} (Dev Mode)`}
                  </ElevatedButton>
                </ButtonContainer>
              </>
            )}

          </PaymentSection>
        )}

        {/* Footer disclosure text - moved inside scrollable area to prevent overlap */}
        {selectedPlan && (
          <DisclosureText>
            All plans auto-renew. You can cancel anytime.<br />
            Full amount charged at start of each billing period.<br />
            *Prices shown in USD. Actual charges may vary based on exchange rates and taxes.
            {euDisclosure}
            <WarningText>
              {refundPolicy}<br />
              ⚠️ Auto-renewal settings cannot be changed within 48 hours of billing date.
            </WarningText>
            <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--color-text-muted)', borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
              <strong>Privacy & Data Collection:</strong><br />
              We automatically detect your location to comply with local consumer protection laws and determine applicable refund policies.
              This uses your IP address and does not store personal location data.<br />
              <br />
              Payment processing is handled by Stripe. By subscribing, you agree to Stripe's
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-button-primary)' }}>
                Privacy Policy
              </a> and
              <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-button-primary)' }}>
                Terms of Service
              </a>.
            </div>
          </DisclosureText>
        )}
      </ScrollableContent>
    </Modal>
  );
};

export default SubscriptionModal;

const PaymentSection = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.buttonPrimary};
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 1rem;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const PlanSummary = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SelectedPlanTitle = styled.h3`
  color: ${props => props.theme.text};
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const PlanDetails = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DetailItem = styled.li`
  color: ${props => props.theme.textSecondary};
  margin-bottom: 0.5rem;
`;